import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { FiAlertTriangle, FiExternalLink, FiLink, FiRefreshCw, FiTrendingUp, FiLayout } from "react-icons/fi";
import { client } from "../Clients";
import type { PageConnectionEdge, PageConnectionsViewModel, PageExternalLink, Taxonomie } from "../API/Models";
import { Modal } from "./Modal";
import { LoadingSpinner } from "./LoadingSpinner";
import PageConnectionsGraph from "./PageConnectionsGraph";
import { TaxonomyMultiSelect } from "./TaxonomyMultiSelect";
import type { IApplicationState } from "../Redux/Reducers";

type Props = {
	open: boolean;
	onClose: () => void;
	pageId?: string | null;
	slug?: string | null;
	language?: string | null;
};

const formatNode = (edge: PageConnectionEdge["to"] | PageConnectionEdge["from"]) =>
	`${edge?.title || edge?.slug || edge?.id || "Untitled"}${edge?.slug ? ` /${edge.slug}` : ""}`;

type ExtendedConnections = PageConnectionsViewModel & { layout?: PageConnectionEdge[] | null };

/** Page connections inspection modal with visual graph and link lists. */
const PageConnectionsModal: React.FC<Props> = ({ open, onClose, pageId, slug, language }) => {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [data, setData] = useState<PageConnectionsViewModel | null>(null);
	const [includeDynamic, setIncludeDynamic] = useState(false);
	const [dynamicTypeFilter, setDynamicTypeFilter] = useState<string[]>([]);
	const contentTypeConfigs = useSelector((state: IApplicationState) => state.contentTypeConfigs.items);

	const loadConnections = useCallback(async () => {
		if (!pageId && (!slug || !language)) {
			setError("Save the page first to inspect its connections.");
			setData(null);
			return;
		}

		setLoading(true);
		setError(null);
		try {
			const response = await client.connections.apiConnectionsPageGet(
				pageId ?? undefined,
				language ?? undefined,
				slug ?? undefined,
				includeDynamic,
				includeDynamic && dynamicTypeFilter.length > 0 ? dynamicTypeFilter.join(",") : undefined
			);
			setData(response.data ?? null);
		} catch (err) {
			console.error(err);
			setError("Unable to load page connections.");
			setData(null);
		} finally {
			setLoading(false);
		}
	}, [pageId, slug, language, includeDynamic, dynamicTypeFilter]);

	useEffect(() => {
		if (!open) return;
		void loadConnections();
	}, [open, loadConnections]);

	const dynamicTypeOptions = useMemo(() => {
		return (contentTypeConfigs ?? [])
			.filter((item) => item?.id)
			.map(
				(item) =>
					({
						id: item.id ?? "",
						title: item.title ?? item.id ?? ""
					}) as Taxonomie
			);
	}, [contentTypeConfigs]);

	const layoutEdges = useMemo(
		() => ((data as ExtendedConnections | null)?.layout as PageConnectionEdge[] | null) ?? [],
		[data]
	);

	const renderEdgeList = (
		title: string,
		icon: React.ReactNode,
		items?: PageConnectionEdge[],
		emptyLabel?: string
	) => (
		<div className="space-y-2 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
			<div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-slate-500">
				{icon}
				<span>{title}</span>
			</div>
			{!items?.length ? (
				<p className="text-xs text-slate-500">{emptyLabel || "None"}</p>
			) : (
				<ul className="space-y-2 text-sm text-slate-700">
					{items.map((edge, idx) => {
						const target = edge.to ?? edge.from;
						return (
							<li
								key={`${title}-${idx}-${target?.id || target?.slug || idx}`}
								className={`rounded-lg border p-2 ${
									edge.exists ? "border-slate-200" : "border-rose-200 bg-rose-50"
								}`}
							>
								<div className="flex items-center justify-between gap-2">
									<div className="min-w-0">
										<p className="truncate font-semibold text-slate-900">{formatNode(target)}</p>
										{edge.label && <p className="text-xs text-slate-500">{edge.label}</p>}
										{edge.path && (
											<p className="text-[11px] font-mono text-slate-400 break-all">Path: {edge.path}</p>
										)}
									</div>
									{!edge.exists && (
										<span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-700">
											<FiAlertTriangle /> Missing
										</span>
									)}
								</div>
							</li>
						);
					})}
				</ul>
			)}
		</div>
	);

	const renderExternalList = (items?: PageExternalLink[]) => (
		<div className="space-y-2 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
			<div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-slate-500">
				<FiExternalLink /> External links
			</div>
			{!items?.length ? (
				<p className="text-xs text-slate-500">No external links.</p>
			) : (
				<ul className="space-y-2 text-sm text-slate-700">
					{items.map((link, idx) => (
						<li
							key={`ext-${idx}-${link.url}`}
							className="rounded-lg border border-slate-200 bg-white p-2 shadow-sm"
						>
							<p className="font-semibold text-slate-900 break-all">{link.url}</p>
							{link.label && <p className="text-xs text-slate-500">{link.label}</p>}
							{link.path && (
								<p className="text-[11px] font-mono text-slate-400 break-all">Path: {link.path}</p>
							)}
						</li>
					))}
				</ul>
			)}
		</div>
	);

	return (
		<Modal show={open} onHide={onClose} size="full85" centered dialogClassName="min-h-[80vh]">
			<Modal.Header closeButton className="bg-gradient-to-r from-white via-slate-50 to-slate-100">
				<div className="space-y-1">
					<Modal.Title className="text-lg font-semibold text-slate-900">Page connections</Modal.Title>
					<p className="text-sm text-slate-600">
						Incoming pages link to this page, outgoing links originate from it, layout links come from the global header/footer,
						and external links point to other sites.
					</p>
				</div>
				<div className="flex items-center gap-2" />
			</Modal.Header>
			<Modal.Body className="bg-slate-50 flex flex-col min-h-0">
				<div className="mb-3 flex flex-wrap items-center gap-3">
					<label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-600">
						<input
							type="checkbox"
							checked={includeDynamic}
							onChange={(e) => setIncludeDynamic(e.target.checked)}
							className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-400"
						/>
						Load dynamic content
					</label>
					<div className="min-w-[240px] max-w-[320px]">
						<TaxonomyMultiSelect
							label="Dynamic content types"
							placeholder="All dynamic types"
							options={dynamicTypeOptions}
							value={dynamicTypeFilter}
							onChange={setDynamicTypeFilter}
							disabled={!includeDynamic}
							maxVisibleOptions={40}
						/>
					</div>
					<button
						type="button"
						onClick={loadConnections}
						className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
					>
						<FiRefreshCw /> Refresh
					</button>
				</div>
				{error && (
					<div className="mb-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700">
						{error}
					</div>
				)}
				<div className="grid flex-1 min-h-0 gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
					<div className="h-full min-h-[520px]">
						<PageConnectionsGraph
							data={{ ...(data as ExtendedConnections | null), layout: layoutEdges }}
							loading={loading}
							className="h-full min-h-[520px]"
						/>
					</div>
					<div className="space-y-3 h-full min-h-0 overflow-y-auto">
						{loading ? (
							<div className="flex items-center justify-center rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
								<LoadingSpinner />
							</div>
						) : (
							<>
								{renderEdgeList("Outgoing", <FiTrendingUp />, data?.outgoing, "No outgoing links.")}
								{renderEdgeList("Incoming", <FiLink />, data?.incoming, "No incoming links from other pages.")}
								{renderEdgeList(
									"Layout",
									<FiLayout />,
									layoutEdges,
									"No layout entries pointing to this page."
								)}
								{renderExternalList(data?.external)}
							</>
						)}
					</div>
				</div>
			</Modal.Body>
		</Modal>
	);
};

export default PageConnectionsModal;

